// src/components/core/TableRegistration.tsx
import React, { useState } from "react";
import { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import DataTable from "@/core/data-tables/DataTable";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "@ui/card";
import { Button } from "@ui/button";
import { Database, FileSymlink, Upload } from "lucide-react";
import { useTableRegistration } from "@/core/registration/register_data";
import RegistrationBanner from "@/core/registration/registrationBanner";

interface Props {
  conn: AsyncDuckDBConnection | null;
  onRefresh: () => Promise<void>;
  onSchemaLoad: (tableName: string) => Promise<void>;
  setMsg: (msg: string) => void;
}

export default function TableRegistration({
  conn,
  onRefresh,
  onSchemaLoad,
  setMsg,
}: Props) {
  const [tableName, setTableName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [remoteUrl, setRemoteUrl] = useState("");
  const [inlineJson, setInlineJson] = useState("");
  const [showJson, setShowJson] = useState(false);

  const { registerTable, loading, result, sampleData, sampleCols } =
    useTableRegistration(conn, setMsg);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setUploadFile(file);
    if (file) {
      setTableName(file.name.split(".")[0].replace(/[^A-Za-z0-9_]/g, "_"));
    }
  };

  const handleRegister = async () => {
    await registerTable(tableName, uploadFile, remoteUrl, inlineJson);
    if (result?.success) {
      await onRefresh();
      await onSchemaLoad(tableName);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-brand" />
          Register New Table
        </CardTitle>
        <CardDescription>
          Provide one of: remote URL, pasted JSON, or a local file (CSV /
          Parquet / JSON) to create a DuckDB table.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* table name */}
        <div className="grid gap-2">
          <label className="text-sm font-medium">Table Name</label>
          <input
            className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="e.g. sales_data"
          />
        </div>

        {/* remote url */}
        <div className="grid gap-2">
          <label className="text-sm font-medium">Remote File URL</label>
          <input
            className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
            value={remoteUrl}
            onChange={(e) => setRemoteUrl(e.target.value)}
            placeholder="https://example.com/data.json"
          />
          <p className="text-xs text-muted-foreground">
            Supported formats: CSV, Parquet, JSON. URL takes precedence.
          </p>
        </div>

        {/* JSON toggle button */}
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowJson((v) => !v)}
          >
            {showJson ? "Hide JSON" : "Paste JSON"}
          </Button>
        </div>

        {/* inline JSON editor */}
        {showJson && (
          <div className="grid gap-2">
            <label className="text-sm font-medium">Paste JSON (optional)</label>
            <textarea
              rows={4}
              className="w-full rounded-md border px-3 py-2 text-sm font-mono"
              placeholder='[{"id":1,"name":"Alice"},{"id":2,"name":"Bob"}]'
              value={inlineJson}
              onChange={(e) => setInlineJson(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Raw JSON will be uploaded as an in-memory file. Leave blank if you
              prefer a remote URL or local file.
            </p>
          </div>
        )}

        {/* local upload */}
        <div className="grid gap-2">
          <label className="text-sm font-medium">Local File (optional)</label>
          <label
            htmlFor="fileUpload"
            className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm hover:bg-muted/60"
          >
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              {uploadFile ? uploadFile.name : "Click to select a file"}
            </span>
          </label>
          <input
            id="fileUpload"
            type="file"
            accept=".csv,.parquet,.json"
            onChange={handleFileChange}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground">
            Supported formats: CSV, Parquet, JSON.
          </p>
        </div>

        {/* banner */}
        <RegistrationBanner result={result} onRetry={handleRegister} />

        {/* action */}
        <Button
          onClick={handleRegister}
          disabled={
            loading ||
            (!remoteUrl.trim() && !inlineJson.trim() && !uploadFile) ||
            !tableName.trim()
          }
          className="w-full"
          variant="brand"
        >
          {loading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background/30 border-t-background" />
              Registering…
            </>
          ) : (
            <>
              <FileSymlink className="mr-2 h-4 w-4" />
              Register Table
            </>
          )}
        </Button>
      </CardContent>

      {sampleData.length > 0 && (
        <CardFooter className="flex-col items-start border-t pt-6">
          <h3 className="mb-4 text-sm font-medium">Sample Data (5 rows)</h3>
          <DataTable
            data={sampleData}
            columns={sampleCols}
            maxHeight="500px"
            colorScheme="blue"
            improveColumnNames
          />
        </CardFooter>
      )}
    </Card>
  );
}
